import { FiEye, FiEyeOff, FiSettings, FiTag } from '@/components/react-icons/main';
import RightPaneFieldV2 from '@/components/v2/RightPaneFieldV2';
import RowV2 from '@/components/v2/RowV2';
import credentialActions from '@/redux/actions/credentialActions.js';
import argumentSelectors from '@/redux/selectors/argumentSelectors';
import selectors from '@/redux/selectors/selectors';
import argumentDataSources from '@/schema/argumentDataSources';
import dataTypeIcons from '@/screens/workflows/schema/dataTypeIcons';
import { camelToTitleCase } from '@shared/utils/camelToTitleCase.js';
import { getAuthByCredential } from '@shared/utils/getAuthByCredential.js';
import { getOAuth2MachineToMachineFields } from '@shared/utils/getOAuth2MachineToMachineFields.js';
import { getStandardAuthFields } from '@shared/utils/getStandardAuthFields.js';
import { sortByProp } from '@shared/utils/sortByProp.js';
import { forwardRef, useImperativeHandle, useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Panel from '../panel/Panel.jsx';
import DividerV2 from '../v2/DividerV2.jsx';
import IconV2 from '../v2/IconV2.jsx';
import Stack from '../v3/Stack.jsx';
import ArgumentInputRowLabel from './ArgumentInputRowLabel';
import ArgumentLabel from './ArgumentLabel.jsx';
import ArgumentWrapper from './ArgumentWrapper.jsx';
import CredentialFileField from './CredentialFileField.jsx';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import getWorkflowAdvancedFields from './lib/getWorkflowAdvancedFields.js';
import getFieldType from './lib/getFieldType.js';
import AuthCredentialsNotesSection from './AuthCredentialsNotesSection';
import { resolveCredentialSaveMeta } from './lib/resolveCredentialSaveMeta.js';

const placeholderMap = {
  string: 'Text',
  number: '0',
  boolean: 'Disabled',
  date: ''
};

const getComputedFields = (secrets, authType, flatFields) => {
  if (authType !== 'oAuth2') {
    return [];
  }

  const fields = ['scope', 'clientId', /Token$/, /Key$/];
  const keys = Object.keys(secrets).filter((secret) => {
    if (flatFields?.some((field) => field.key === secret)) {
      return false;
    }
    return fields.some((field) => {
      if (field instanceof RegExp) {
        return field.test(secret);
      }
      return field === secret;
    });
  });

  return keys.map((key) => ({ key, name: camelToTitleCase(key), value: secrets[key], dataType: 'string' }));
};

const validateCredentials = (appArgs, state) => {
  const invalids = appArgs
    ?.filter((arg) => !!arg.required)
    .filter((arg) => {
      const value = state[arg.key];
      return !value || value === '';
    });

  return (invalids?.length === 0 || !invalids) && Object.keys(state).length > 0;
};

const ArgumentFields = ({ fields, secrets, meta, saveFn, disabled, credentialSlider, allVisible = false }) => {
  const [visibleFields, setVisibleFields] = useState({});

  const toggleFieldVisibility = (fieldKey) => {
    setVisibleFields((prev) => ({ ...prev, [fieldKey]: !prev[fieldKey] }));
  };

  if (credentialSlider) {
    return (
      <>
        {fields.map((field, idx) => {
          if (field.dataType === 'file') {
            return (
              <ArgumentWrapper key={idx}>
                <ArgumentLabel //
                  label={field.name || field.placeholder}
                  title={field.name || field.placeholder}
                  isRequired={field?.required}
                  tag={field?.tag}
                  icon={field?.showDataType === false ? undefined : dataTypeIcons[field.dataType]}
                  description={field.description}
                  variant={field.variant}
                />
                <div className="credential-arg-row">
                  <CredentialFileField field={field} value={secrets?.[field.key]} onChange={(value) => saveFn?.({ key: field.key, value })} disabled={disabled} />
                </div>
              </ArgumentWrapper>
            );
          }

          const fieldType = getFieldType(field, secrets);
          const shouldShowIcon = !allVisible && field?.isSensitive !== false && ['input', 'textarea', 'password'].includes(fieldType);
          const isVisible = allVisible || visibleFields[field.key];
          const inputType = shouldShowIcon && !isVisible ? 'password' : fieldType === 'password' ? 'input' : fieldType;

          return (
            <ArgumentWrapper key={idx}>
              <ArgumentLabel //
                label={field.name || field.placeholder}
                title={field.name || field.placeholder}
                isRequired={field?.required}
                tag={field?.tag}
                icon={field?.showDataType === false ? undefined : dataTypeIcons[field.dataType || 'string']}
                description={field.description}
                variant={field.variant}
              />
              <div className="credential-arg-row">
                <RightPaneFieldV2 //
                  placeholder={field.placeholder || placeholderMap[field.dataType]}
                  value={secrets?.[field.key]}
                  type={inputType}
                  onChange={(value) => saveFn?.({ key: field.key, value })}
                  options={field.dataType === 'list' ? field.options : undefined}
                  nullable={true}
                  nullableLabel={field?.nullableLabel}
                  showSaveButton={false}
                  disabled={disabled}
                  borderless={false}
                  autoSize={true}
                  autoComplete={'cc-number'}
                  allowMultiline={fieldType !== 'password'}
                />
                {shouldShowIcon && (
                  <div className="credential-arg-row-icon-wrapper" onClick={() => toggleFieldVisibility(field.key)}>
                    <IconV2 className="light-medium" icon={isVisible ? FiEyeOff : FiEye} title={isVisible ? 'Hide value' : 'Show value'} />
                  </div>
                )}
              </div>
            </ArgumentWrapper>
          );
        })}
      </>
    );
  }
  return (
    <>
      {fields.map((field) => {
        const fieldType = getFieldType(field, secrets);

        return (
          <RowV2 key={field.id || field.key} variant={'workflow-panel'}>
            <ArgumentInputRowLabel //
              label={field.name || field.placeholder}
              title={field.name || field.placeholder}
              icon={dataTypeIcons[field.dataType || 'string']}
              description={field.description}
            />
            {field.dataType === 'file' ? (
              <CredentialFileField field={field} value={secrets?.[field.key]} onChange={(value) => saveFn?.({ key: field.key, value })} disabled={disabled} />
            ) : (
              <RightPaneFieldV2 //
                placeholder={field.placeholder || placeholderMap[field.dataType]}
                value={secrets?.[field.key] ?? meta?.[field.key] ?? field.defaultValue}
                type={fieldType}
                onChange={(value) => saveFn?.({ key: field.key, value })}
                options={field.dataType === 'list' ? field.options : undefined}
                nullable={true}
                nullableLabel={field?.nullableLabel}
                showSaveButton={false}
                disabled={disabled}
                autoSize={true}
                allowMultiline={fieldType !== 'password'}
              />
            )}
          </RowV2>
        );
      })}
    </>
  );
};

const ArgumentsCredentialsWrapper = forwardRef(({ dataSource, initialSecrets, formSecrets, appId, appVersion, credentialId, onChange, directSave = false, disabled = false, showComputedFields = false, credentialSlider = false }, ref) => {
  const [showComputed, setShowComputed] = useState(false);

  const useArgument = dataSource && !appId;

  const argumentApp = useSelector(argumentSelectors[dataSource].activeApp);
  const fullApp = useSelector((state) => selectors.appById(state, appId, appVersion));
  const app = useArgument ? argumentApp : fullApp;

  const argumentCredential = useSelector((state) => argumentSelectors[dataSource].activeCredential(state, credentialId, app?.id));
  const fullCredential = useArgSelector(selectors.credentialById, credentialId);
  const credential = useArgument ? argumentCredential : fullCredential;

  const appAuthentication = app?.authentication;
  const argumentAuth = useArgSelector(argumentSelectors[dataSource].activeAuth, credentialId);
  const fullAuth = getAuthByCredential({ authentication: appAuthentication, credential });
  const auth = useArgument ? argumentAuth : fullAuth;

  const companyId = useSelector(selectors.companyId);
  const timezones = useSelector((state) => state.ui.timezones);
  const environments = useSelector(selectors.environments);

  const authType = auth?.type;
  const credId = credentialId || credential?.credentialId;
  const meta = credential?.meta;
  const secrets = formSecrets || credential?.secrets;
  const args = auth?.arguments;
  const isAlreadyDefault = credential?.isDefault;
  const stringifiedSecrets = JSON.stringify(secrets || {});

  const metaFields = [{ key: 'label', name: 'Friendly Name', placeholder: `Erlich's ${app?.name} Account`, dataType: 'string', showDataType: false }];
  const advancedFields = getWorkflowAdvancedFields({ timezones, environments, dataSource });

  const additionalFields = args
    ?.filter((arg) => arg?.hidden !== true) //
    ?.sort(sortByProp({ key: 'sequence', type: 'number' }, 'asc', (n) => (!n ? 500 : n)))
    ?.map((arg) => {
      if (arg?.dataType !== 'list' || !arg?.list) {
        return arg;
      }

      const list = arg?.list;

      if (list?.fixedOptions) {
        return {
          ...arg,
          options: Object.entries(list.fixedOptions)
            .map(([value, label]) => {
              if (typeof label === 'object' && label !== null && 'label' in label) {
                return { label: label.label, value, sequence: typeof label.sequence === 'number' ? label.sequence : Number.MAX_SAFE_INTEGER };
              }
              return { label, value, sequence: Number.MAX_SAFE_INTEGER };
            })
            .sort((a, b) => (a.sequence || Number.MAX_SAFE_INTEGER) - (b.sequence || Number.MAX_SAFE_INTEGER))
        };
      }

      return arg;
    })
    ?.map((arg) => [{ ...arg, id: arg.key, placeholder: arg.name }])
    ?.filter((arg) => !!arg?.[0]?.id);
  const baseAuthFields = getStandardAuthFields({ authType, auth });

  const fields = [
    ...baseAuthFields, //
    ...getOAuth2MachineToMachineFields({
      auth,
      existingFields: [...baseAuthFields, ...(additionalFields || []).flat()]
    }),
    ...(additionalFields || [])
  ];

  const flatFields = fields.flat();
  const basicFields = flatFields.filter((field) => !field?.isAdvanced);
  const advancedFieldsFromArgs = flatFields.filter((field) => field?.isAdvanced);
  const allAdvancedFields = [...advancedFieldsFromArgs, ...advancedFields];
  const hasAdvancedValues = allAdvancedFields?.some((field) => !!secrets?.[field.key]);
  const computedFields = useMemo(() => showComputedFields && getComputedFields(secrets, authType, flatFields), [showComputedFields, secrets, authType, flatFields]);

  const onSave = ({ key, value, fullSecrets, isDefault, appId }, callback) => {
    if (disabled) {
      return;
    }
    const newSecrets = fullSecrets || { ...secrets, [key]: value };
    const isValid = validateCredentials(args, newSecrets);

    const defaultChanged = isAlreadyDefault !== isDefault;
    const secretsChanged = initialSecrets ? JSON.stringify(initialSecrets) !== stringifiedSecrets : JSON.stringify(newSecrets) !== stringifiedSecrets;

    const changedSecrets = Object.keys(credential?.secrets || {}).filter((key) => credential?.secrets?.[key] !== newSecrets?.[key]);
    const BLACKLISTED_KEYS = ['label', 'environmentId', 'timezone'];
    const hasBlacklistedChanges = changedSecrets?.length === 0 ? true : changedSecrets.some((key) => BLACKLISTED_KEYS.includes(key));

    if (defaultChanged || secretsChanged) {
      // Determine correct companyId:
      // - For existing credentials: always preserve original companyId
      // - For new credentials in app editor: use 'MINDCLOUD'
      // - For new credentials elsewhere: use selector companyId
      const isAppAuthenticationInstance = dataSource === argumentDataSources.appAuthenticationInstance;
      const isNewCredential = !credential?.companyId;
      const targetCompanyId = isNewCredential && isAppAuthenticationInstance ? 'MINDCLOUD' : credential?.companyId || companyId;

      const { meta: nextMeta } = resolveCredentialSaveMeta({
        credentialMeta: credential?.meta,
        currentSecrets: secrets,
        nextSecrets: newSecrets,
        changedKey: key
      });

      credentialActions.set({
        appId: app?.id || credential?.appId || appId,
        companyId: targetCompanyId,
        credentialId: credId,
        status: hasBlacklistedChanges ? credential?.status : 'unknown',
        valid: authType === 'oAuth2' ? credential?.status === 'valid' : isValid,
        isDefault: isDefault ?? credential?.isDefault ?? false,
        secrets: { type: authType, ...newSecrets },
        meta: nextMeta
      });
    }
    callback?.();
  };

  useImperativeHandle(ref, () => ({ onSave }));

  const saveFn = directSave ? onSave : onChange || (() => {});

  if (credentialSlider) {
    const AdvancedSettingsTitle = () => (
      <div className="boxed-title">
        <div className="icon">
          <IconV2 icon={FiSettings} size={16} />
        </div>
        <div className="text">
          <div className="title">Advanced Settings</div>
          <div className="description">Optional configuration for specific setups</div>
        </div>
      </div>
    );

    const LabelSettingsTitle = () => {
      const [title, setTitle] = useState('Connection Name');

      useEffect(() => {
        if (secrets?.label) {
          setTitle('Edit Connection Name');
        }
      }, []);

      return (
        <div className="boxed-title">
          <div className="icon">
            <IconV2 icon={FiTag} size={16} />
          </div>
          <div className="text">
            <div className="title">{title}</div>
            <div className="description">Used to tell multiple connections apart</div>
          </div>
        </div>
      );
    };

    return (
      <>
        <AuthCredentialsNotesSection auth={auth} />
        {basicFields?.length > 0 && (
          <Panel.Section className={'simple-arguments'} section="secrets" title="Secrets" headerless>
            <ArgumentFields fields={basicFields} secrets={secrets} meta={meta} saveFn={saveFn} disabled={disabled} credentialSlider={credentialSlider} />
          </Panel.Section>
        )}
        <Panel.Section className={'simple-arguments boxed'} section="meta" title={LabelSettingsTitle} collapsable collapseByDefault>
          <ArgumentFields autoFocus fields={metaFields} secrets={secrets} meta={meta} saveFn={saveFn} disabled={disabled} credentialSlider={credentialSlider} allVisible={true} />
        </Panel.Section>
        {allAdvancedFields?.length > 0 && (
          <Panel.Section className={'simple-arguments boxed'} section="advanced" title={AdvancedSettingsTitle} collapsable collapseByDefault>
            <ArgumentFields fields={allAdvancedFields} secrets={secrets} meta={meta} saveFn={saveFn} disabled={disabled} credentialSlider={credentialSlider} allVisible={true} />
          </Panel.Section>
        )}
      </>
    );
  }

  return (
    <>
      <AuthCredentialsNotesSection auth={auth} />
      <ArgumentFields fields={metaFields} secrets={secrets} meta={meta} saveFn={saveFn} disabled={disabled} credentialSlider={credentialSlider} />
      {basicFields?.length > 0 && (
        <>
          <DividerV2 style={{ margin: '8px 0' }} />
          <ArgumentFields fields={basicFields} secrets={secrets} meta={meta} saveFn={saveFn} disabled={disabled} credentialSlider={credentialSlider} />
        </>
      )}
      {allAdvancedFields?.length > 0 && (
        <>
          <DividerV2 style={{ margin: '8px 0' }} />
          <Panel.Section collapsable={true} collapseByDefault={!hasAdvancedValues} title={'Advanced Properties'}>
            <ArgumentFields fields={allAdvancedFields} secrets={secrets} meta={meta} saveFn={saveFn} disabled={disabled} credentialSlider={credentialSlider} />
          </Panel.Section>
        </>
      )}
      {showComputedFields && computedFields?.length > 0 && (
        <>
          <DividerV2 style={{ margin: '16px 0 8px 0' }} />
          <Stack justify={'start'} align={'center'} gap={4}>
            <p className={'computed'}>Computed</p>
            <IconV2 className={'computed-toggle'} title={showComputed ? 'Hide computed fields' : 'Show computed fields'} icon={showComputed ? FiEye : FiEyeOff} onClick={() => setShowComputed(!showComputed)} />
          </Stack>
          {showComputed && <ArgumentFields fields={computedFields} secrets={secrets} meta={meta} saveFn={saveFn} disabled={true} credentialSlider={credentialSlider} />}
        </>
      )}
    </>
  );
});

ArgumentsCredentialsWrapper.displayName = 'ArgumentsCredentialsWrapper';

export default ArgumentsCredentialsWrapper;
